import { useContext, useEffect, useState } from "react";
import { RecipientDeliveryProp } from "./RecipientDelivery";
import { CMSModal } from "@/context";
import { twMerge } from "tailwind-merge";
import {
  DeliveryEnum,
  GetDeliveriesDocument,
  useDeleteDeliveryMutation,
  useGetUsersLazyQuery,
  User,
  useUpdateDeliveryMutation,
} from "@/graphql/generated/schema";
import Input from "@/designs/Input";
import { CheckCircle, CircleX, Trash } from "lucide-react";
import moment from "moment";
import Button from "@/designs/Button";
import Loader from "@/designs/Loader";
import { useTranslation } from "react-i18next";

const GeneralDelivery = ({ delivery, onClose }: RecipientDeliveryProp) => {
  const { theme } = useContext(CMSModal);
  const { t } = useTranslation();
  const [note, setNote] = useState(delivery?.note ?? "");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setNote(delivery?.note ?? "");
  }, [delivery?.id]);

  const isNoteDirty = note !== (delivery?.note ?? "");
  const [selectedEmployee, setSelectedEmployee] = useState<User | null>(null);

  const [employee, { data, loading }] = useGetUsersLazyQuery();
  const employees = data?.getUsers?.user || [];
  const handleSearch = (searchTerm: string) => {
    setSearch(searchTerm);
    employee({ variables: { search: searchTerm } });
  };
  const [updateDelivery, { loading: updateLoading }] =
    useUpdateDeliveryMutation({
      refetchQueries: [GetDeliveriesDocument],
    });

  const [deleteDelivery, { loading: deleteLoading }] =
    useDeleteDeliveryMutation({
      refetchQueries: [GetDeliveriesDocument],
    });

  const saveNote = async () => {
    await updateDelivery({
      variables: {
        input: { _id: delivery?.id, note, location: delivery?.location?.id as string },
      },
    });
  };

  const deleteData = async () => {
    await deleteDelivery({
      variables: {
        id: delivery?.id,
      },
    });
    onClose();
  };
  const updateNominee = async () => {
    await updateDelivery({
      variables: {
        input: {
          _id: delivery?.id,
          reciepient: selectedEmployee?.id,
          deliveryType: DeliveryEnum.Recipient,
          signature: delivery?.signature,
          notify: true,
          location: delivery?.location?.id||"",
        },
      },
    });
    onClose();
  };
  return (
    <div className="space-y-6">
      {(updateLoading || deleteLoading) && <Loader />}
      <div className="space-y-2">
        <h2
          className={twMerge(
            "text-lg font-bold transition-colors",
            theme === "dark" ? "text-gray-100" : "",
          )}
        >
          {t("deliveries.viewDelivery.generalTitle")}
        </h2>
        <label
          className={twMerge(
            "text-sm",
            theme === "dark" ? "text-gray-300" : "text-gray-600",
          )}
        >
          {t("deliveries.viewDelivery.assignLabel")}
        </label>
      </div>
      <Input
        label={t("deliveries.addDelivery.recipient")}
        placeholder={t("deliveries.addDelivery.searchPlaceholder")}
        disabled={delivery?.collected || false}
        onChange={(value) => handleSearch(value)}
        value={search}
        icon={
          selectedEmployee && (
            <CircleX
              className=" cursor-pointer hover:block"
              size={16}
              onClick={() => {
                setSearch("");
                setSelectedEmployee(null);
              }}
            />
          )
        }
      />
      {loading && (
        <div className="text-sm text-gray-400">
          {t("deliveries.addDelivery.searching")}
        </div>
      )}
      {!loading && search && employees.length === 0 && (
        <div className="text-sm text-gray-400">
          {t("deliveries.addDelivery.noResults", { search })}
        </div>
      )}
      {!loading && !selectedEmployee && search && employees.length > 0 && (
        <div
          className={twMerge(
            "border rounded-md max-h-48 overflow-auto",
            theme === "dark"
              ? "border-gray-700 bg-[#1f1f1f]"
              : "border-gray-200 bg-white",
          )}
        >
          {employees.map((emp: any) => (
            <div
              key={emp.id}
              className={twMerge(
                "px-3 py-2 text-sm cursor-pointer transition-colors",
                theme === "dark"
                  ? "hover:bg-gray-700 text-gray-200"
                  : "hover:bg-gray-100 ",
              )}
              onClick={() => {
                setSelectedEmployee(emp);
                setSearch(`${emp.firstName} ${emp.lastName}`);
              }}
            >
              <div className="font-medium">
                {emp.firstName} {emp.lastName}
              </div>
              <div className="text-xs opacity-70">{emp.email}</div>
            </div>
          ))}
        </div>
      )}
      <Input
        label={t("deliveries.viewDelivery.dateDelivered")}
        value={moment(delivery?.delivered).format("MMMM Do,h:mma") || ""}
        readonly
      />
      {delivery?.collected && (
        <div className="space-y-2">
          {delivery?.signature ? (
            <span
              className={twMerge(
                "flex items-center gap-2",
                theme === "dark" ? "text-gray-300" : "",
              )}
            >
              <CheckCircle color="green" />
              {t("deliveries.viewDelivery.signatureRequired")}
            </span>
          ) : (
            <span
              className={twMerge(
                "flex items-center gap-2",
                theme === "dark" ? "text-gray-300" : "",
              )}
            >
              <CircleX color="gray" />
              {t("deliveries.viewDelivery.signatureNotRequired")}
            </span>
          )}
          <div
            className={twMerge(
              "flex items-center gap-2",
              theme === "dark" ? "text-gray-300" : "",
            )}
          >
            <CheckCircle color="green" />
            {t("deliveries.viewDelivery.notificationSent")}{" "}
            {!delivery?.collected && (
              <span className="cursor-pointer text-[#099ed1]">
                {t("deliveries.viewDelivery.resend")}
              </span>
            )}
          </div>
          <div
            className={twMerge(
              "flex items-center gap-2",
              theme === "dark" ? "text-gray-300" : "",
            )}
          >
            {" "}
            <CheckCircle color="green" />
            {t("deliveries.viewDelivery.collectedAt", {
              date: moment(delivery?.collectedAt).format("h:mma, MMM Do"),
            })}
          </div>
        </div>
      )}
      <div className="space-y-1">
        <label
          className={twMerge(
            "block text-sm font-medium transition-colors",
            theme === "dark" ? "text-gray-300" : "text-gray-700",
          )}
        >
          {t("deliveries.viewDelivery.note")}
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("deliveries.viewDelivery.notePlaceholder")}
          rows={3}
          className={twMerge(
            "px-3 py-2 border rounded-md w-full transition-colors focus:outline-none focus:ring-1 focus:ring-[#099ed1] resize-none text-sm",
            theme === "dark"
              ? "bg-[#191919] border-gray-700 text-gray-100 placeholder-gray-600"
              : "bg-white border-gray-300",
          )}
        />
        {isNoteDirty && (
          <div className="flex justify-end">
            <Button className="w-32" onClick={saveNote} loading={updateLoading}>
              {t("deliveries.viewDelivery.saveNote")}
            </Button>
          </div>
        )}
      </div>
      {!delivery?.collected && (
        <div className="flex items-center justify-between">
          <span
            className="flex items-center gap-2 text-red-400 cursor-pointer"
            onClick={deleteData}
          >
            <Trash size={18} /> {t("deliveries.viewDelivery.deleteButton")}
          </span>

          <Button
            className="w-40"
            disabled={!selectedEmployee}
            onClick={updateNominee}
          >
            {t("deliveries.viewDelivery.notifyButton")}
          </Button>
        </div>
      )}
    </div>
  );
};
export default GeneralDelivery;
